// A BYOK key verify's answer (plan 101 rows 05-10 / 17-15), shared by the org
// page (`POST /v1/billing/keys/:id/verify`) and My keys (`POST /v1/me/keys/:id/verify`).
//
// Both answer 200 with the masked key row PLUS `{ outcome, reason, fault }`:
//   - `verified`         the key passed; `reason` is null.
//   - `transport_failed` the provider was unreachable. NOT a pass: the key keeps
//                        its prior status and the worker re-verifies it.
//   - `model_parked`     the credential is good, the model's leg failed.
// `invalid` stays a 422 problem (thrown by `apiFetch`, the caller's error toast).
//
// `fault` is the probe's fault on `model_parked`, null otherwise (#6657). By ruling
// (#3961) the outcome is `model_parked` too when OUR egress gate refused the model's
// endpoint (`egress_refused`): the model was never asked, so that toast names the
// gate, and it branches on `fault`, never on the `reason` text.
//
// A 200 used to read as success, so an unreachable provider toasted "Key
// verified". Only `outcome: 'verified'` is a pass here, and the body is parsed:
// a 200 without a known outcome THROWS, so it lands on the caller's failure
// toast instead of the success one.
//
// THIS MODULE also owns the dashboard's ONE probe-fault routing: which family of
// copy tells a fault. The toast (here) reads it. The key card does NOT branch —
// a persisted row carries no fault (`byok_keys` has no column for one, #6657),
// so its frame is the neutral `last_error` sentence for every writer. The fault
// is STRUCTURED DATA on the wire; the reason sentence is never parsed, because
// a sentence that changes is a sentence a card stops reading.

import { z } from 'zod';
import { t } from '../i18n';
import type { ProbeFault } from './keys-types';
import type { ToastTone } from './toast';

const KeyVerifyAnswerSchema = z.object({
  outcome: z.enum(['verified', 'transport_failed', 'model_parked']),
  reason: z.string().nullable(),
  // A STRING, not the fault enumeration: a fault kind this build has never
  // heard of must not make a 200 unparseable (it would turn a parked model
  // into a failed verify). The routing below is where a fault is recognised.
  fault: z.string().nullable(),
});

export type KeyVerifyAnswer = z.infer<typeof KeyVerifyAnswerSchema>;

/**
 * Which family of copy tells a probe fault:
 *   - `egress` — OUR egress gate refused the endpoint, so the model was NEVER
 *     asked (#3961). The copy names the gate, never the provider.
 *   - `model` — the provider's or the model's leg failed. The copy names the
 *     model.
 */
export type ProbeFaultRoute = 'egress' | 'model';

/**
 * EVERY probe fault, and where it is told (#6657).
 *
 * A `Record`, so the vocabulary is EXHAUSTIVE BY COMPILER: a fault kind added
 * to {@link ProbeFault} without a row here is a type error, and a row here that
 * names a kind the vocabulary does not carry is a type error too. That is the
 * guard — the shape of the mistake this module exists to prevent, a new fault
 * kind silently falling through to the model's copy.
 *
 * The three kinds that never arrive on a `model_parked` outcome keep the
 * `model` row because they are the only copy this app owns for a failed probe
 * leg, and because their OWN outcomes are decided upstream: `unreachable` is a
 * `transport_failed` (the toast above has a copy for it) and `quota` is a
 * key-level verdict (`credits_exhausted` / `rate_limited`), never a parked
 * model. `auth` CAN arrive on a `model_parked` — a 403 after the auth leg
 * passed is the model's leg failing — and reads as the model's leg, which is
 * what it is.
 */
const FAULT_ROUTE: Record<ProbeFault, ProbeFaultRoute> = {
  unreachable: 'model',
  bad_shape: 'model',
  quota: 'model',
  auth: 'model',
  egress_refused: 'egress',
};

/** Is this wire string a fault kind this build knows? */
function isProbeFault(value: string): value is ProbeFault {
  return Object.hasOwn(FAULT_ROUTE, value);
}

/**
 * Where a probe fault is told. An ABSENT or UNKNOWN fault routes like every
 * non-egress fault — the provider's or the model's leg failed — because that
 * is the only reading a fault kind we cannot name supports, and because
 * guessing "our gate" on an unknown kind would blame us for a provider's
 * outage. Never derived from the reason text.
 */
export function probeFaultRoute(fault: string | null | undefined): ProbeFaultRoute {
  if (fault === null || fault === undefined) return 'model';
  return isProbeFault(fault) ? FAULT_ROUTE[fault] : 'model';
}

/** The verify body's outcome, or a throw naming what came back instead. */
export function parseKeyVerify(body: unknown): KeyVerifyAnswer {
  const parsed = KeyVerifyAnswerSchema.safeParse(body);
  if (!parsed.success) {
    throw new Error(
      `key verify answered 200 without a known { outcome, reason, fault }: ${parsed.error.message}`,
    );
  }
  return parsed.data;
}

/** The toast one verify outcome earns. */
export function keyVerifyNotice(answer: KeyVerifyAnswer): {
  readonly message: string;
  readonly tone: ToastTone;
} {
  const reason = answer.reason ?? '';
  switch (answer.outcome) {
    case 'verified':
      return { message: t('account_keys.verified_toast'), tone: 'success' };
    case 'transport_failed':
      return { message: t('account_keys.verify_unreachable_toast', { reason }), tone: 'warning' };
    case 'model_parked':
      return probeFaultRoute(answer.fault) === 'egress'
        ? { message: t('account_keys.verify_egress_refused_toast', { reason }), tone: 'warning' }
        : { message: t('account_keys.verify_model_parked_toast', { reason }), tone: 'warning' };
  }
}
